import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth, useRole, useStaffPermissions } from '@structural-climbing/auth'
import { Button, Card, CardBody, CardHeader, Chip, Spinner } from '@structural-climbing/ui'
import type { SubscriptionStatus } from '@structural-climbing/ui'
import { useGymThemeContext } from '../../layouts/useGymTheme'
import { MemberAvatar } from './MemberAvatar'
import { MemberIdentity } from './MemberIdentity'
import { SubBadge } from './SubBadge'
import { formatDuration } from './formatDuration'
import {
  CurrentSubscriptionCard,
  PreviousSubscriptionRow,
  type ProfileSubscriptionData,
} from './ProfileSubscription'
import { AddonsSection } from './AddonsSection'
import { FreezeSubscriptionModal } from './FreezeSubscriptionModal'
import { RenewalSection } from './RenewalSection'
import { ageFrom } from './ageFrom'
import { ProfileDetails, formatDetailDate, usePersonDetailItems } from './ProfileDetails'
import { TrainerAssignmentsSection } from '../trainers/TrainerAssignmentsSection'
import { GymDialog } from '../../components/GymDialog'

interface ChildDetails {
  childId: string
  firstName: string
  lastName: string | null
  birthDate: string | null
  gender?: string | null
  addedAt?: string | null
  /** The child signs in with a login of their own. */
  hasAccount?: boolean
  isCheckedIn: boolean
  currentSubscription: ProfileSubscriptionData | null
  /** The renewal bought to follow the current plan. */
  upcomingSubscription?: ProfileSubscriptionData | null
  addons?: ProfileSubscriptionData[]
  previousSubscriptions: ProfileSubscriptionData[]
  recentCheckIns: { id: string; checkedInAt: string; checkedOutAt: string | null }[]
  parent: {
    userId: string
    name: string
    email: string
    nickname: string | null
    avatarExt: string | null
    avatarUpdatedAt: string | null
    subscriptionStatus: SubscriptionStatus | null
  }
}

interface Props {
  childId: string
  onClose: () => void
  onOpenParent: (userId: string) => void
  /** A change made here (a freeze) that the page underneath should re-read. */
  onChanged?: () => void
  /** Shown to look at, with every action hidden. See MemberProfileModal. */
  readOnly?: boolean
}

const sectionLabel = 'mb-1.5 text-xs font-semibold uppercase tracking-wide text-foreground/40'

export function ChildProfileModal({
  childId,
  onClose,
  onOpenParent,
  onChanged,
  readOnly = false,
}: Props) {
  const { getAccessTokenSilently } = useAuth()
  const { t } = useTranslation()
  const { primaryColor } = useGymThemeContext()
  const apiUrl = import.meta.env.VITE_API_URL as string

  const [data, setData] = useState<ChildDetails | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const details = usePersonDetailItems()

  // Same rule as the adult profile: freezing sits with whoever manages
  // subscriptions, and the endpoint refuses a trainer regardless.
  const role = useRole()
  const staffPermissions = useStaffPermissions()
  const canFreeze = !readOnly && (role === 'gym_owner' || staffPermissions?.subscriptions === true)
  const [freezeMode, setFreezeMode] = useState<'freeze' | 'unfreeze' | null>(null)

  // `quiet` re-reads after a freeze without swapping the profile for a spinner.
  const load = useCallback(
    async (quiet = false) => {
      if (!quiet) {
        setLoading(true)
        setError(false)
      }
      try {
        const token = await getAccessTokenSilently()
        const res = await fetch(`${apiUrl}/children/${encodeURIComponent(childId)}/details`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        if (res.ok) {
          setData((await res.json()) as ChildDetails)
        } else if (!quiet) {
          setError(true)
        }
      } catch {
        if (!quiet) setError(true)
      } finally {
        if (!quiet) setLoading(false)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [childId],
  )

  useEffect(() => {
    void load()
  }, [load])

  const name = data ? [data.firstName, data.lastName].filter(Boolean).join(' ') : ''

  return (
    <GymDialog onClose={onClose} size="lg">
      <Card className="flex min-h-0 flex-col shadow-none">
        <CardHeader className="flex shrink-0 items-start justify-between gap-3 pb-2">
          {loading ? (
            <div className="flex w-full justify-center py-4">
              <Spinner size="sm" color="primary" />
            </div>
          ) : error || !data ? (
            <p className="text-sm text-danger">{t('common.errorDefault')}</p>
          ) : (
            <div className="flex items-center gap-4">
              <MemberAvatar
                userId={data.parent.userId}
                name={name}
                avatarExt={null}
                avatarUpdatedAt={null}
                size="lg"
                primaryColor={primaryColor}
              />
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-lg font-semibold">{name}</p>
                  <span className="shrink-0 rounded-full bg-primary/10 px-1.5 py-0.5 text-xs text-primary">
                    {t('members.childBadge')}
                  </span>
                </div>
                {data.birthDate && (
                  <p className="text-sm text-foreground/50">
                    {t('members.childAge', { count: ageFrom(data.birthDate) })}
                  </p>
                )}
                <div className="mt-1 flex flex-wrap gap-1">
                  {data.isCheckedIn && (
                    <span className="rounded-full bg-success/10 px-2 py-0.5 text-xs font-medium text-success">
                      {t('members.inGymBadge')}
                    </span>
                  )}
                  <SubBadge status={data.currentSubscription?.status} />
                  {data.hasAccount && (
                    <Chip size="sm" variant="flat" color="primary">
                      {t('members.childHasLogin')}
                    </Chip>
                  )}
                </div>
              </div>
            </div>
          )}
          <Button isIconOnly size="sm" variant="light" onClick={onClose} aria-label="Close">
            <svg
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </Button>
        </CardHeader>

        {data && !loading && (
          <CardBody className="flex flex-col gap-4 overflow-y-auto pt-0">
            <ProfileDetails
              items={[
                details.birth(data.birthDate),
                details.gender(data.gender),
                {
                  label: t('members.detailsAddedOn'),
                  value: data.addedAt ? formatDetailDate(data.addedAt) : null,
                },
              ]}
            />

            {/* Current subscription */}
            <div>
              <p className={sectionLabel}>{t('members.currentSubscription')}</p>
              {data.currentSubscription ? (
                <CurrentSubscriptionCard
                  sub={data.currentSubscription}
                  onFreeze={canFreeze ? () => setFreezeMode('freeze') : undefined}
                  onUnfreeze={canFreeze ? () => setFreezeMode('unfreeze') : undefined}
                />
              ) : (
                <p className="text-sm text-foreground/30">{t('members.noSubscription')}</p>
              )}
              <div className="mt-2">
                <RenewalSection
                  current={data.currentSubscription}
                  upcoming={data.upcomingSubscription ?? null}
                  canRenew={canFreeze}
                  memberEmail={data.parent.email}
                  memberUserId={data.parent.userId}
                  childId={data.childId}
                  childName={[data.firstName, data.lastName].filter(Boolean).join(' ')}
                  onRenewed={() => {
                    void load(true)
                    onChanged?.()
                  }}
                />
              </div>
              {freezeMode && data.currentSubscription && (
                <FreezeSubscriptionModal
                  sub={data.currentSubscription}
                  mode={freezeMode}
                  onClose={() => setFreezeMode(null)}
                  onDone={() => {
                    setFreezeMode(null)
                    void load(true)
                    onChanged?.()
                  }}
                />
              )}
            </div>

            <AddonsSection addons={data.addons ?? []} readOnly={readOnly} />

            {/* Previous subscriptions */}
            {data.previousSubscriptions.length > 0 && (
              <div>
                <p className={sectionLabel}>{t('members.previousSubscriptions')}</p>
                <div className="flex flex-col gap-1">
                  {data.previousSubscriptions.map((sub) => (
                    <PreviousSubscriptionRow key={sub.id} sub={sub} />
                  ))}
                </div>
              </div>
            )}

            {/* Recent check-ins */}
            <div>
              <p className={sectionLabel}>{t('members.recentCheckIns')}</p>
              {data.recentCheckIns.length === 0 ? (
                <p className="text-sm text-foreground/30">{t('members.noCheckIns')}</p>
              ) : (
                <div className="flex flex-col gap-1">
                  {data.recentCheckIns.map((ci) => (
                    <div
                      key={ci.id}
                      className="grid grid-cols-[1fr_auto_auto_auto] items-center gap-3 rounded-lg bg-content2/30 px-3 py-2"
                    >
                      <p className="text-sm text-foreground/70">
                        {new Date(ci.checkedInAt).toLocaleDateString(undefined, {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                        })}
                      </p>
                      <p className="text-sm tabular-nums text-foreground/60">
                        {new Date(ci.checkedInAt).toLocaleTimeString(undefined, {
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </p>
                      <p className="text-sm tabular-nums text-foreground/60">
                        {ci.checkedOutAt
                          ? new Date(ci.checkedOutAt).toLocaleTimeString(undefined, {
                              hour: '2-digit',
                              minute: '2-digit',
                            })
                          : '—'}
                      </p>
                      <span className="rounded-full bg-content3/50 px-2 py-0.5 text-xs font-medium text-foreground/60">
                        {formatDuration(
                          ci.checkedInAt,
                          ci.checkedOutAt,
                          t('members.checkInActive'),
                        )}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Trainers. The assignment hangs off the parent's account, so
                the section reads the family's rows and keeps this child's. */}
            <TrainerAssignmentsSection
              memberUserId={data.parent.userId}
              memberEmail={data.parent.email}
              memberName={name}
              childId={childId}
            />

            {/* Parent */}
            <div>
              <p className={sectionLabel}>{t('members.childParent')}</p>
              <button
                type="button"
                onClick={() => onOpenParent(data.parent.userId)}
                title={t('members.viewProfile')}
                className="flex w-full items-center justify-between gap-2 rounded-lg bg-content2/50 px-3 py-2 text-left transition-colors hover:bg-default/10"
              >
                <MemberIdentity
                  userId={data.parent.userId}
                  name={data.parent.name}
                  nickname={data.parent.nickname}
                  email={data.parent.email}
                  avatarExt={data.parent.avatarExt}
                  avatarUpdatedAt={data.parent.avatarUpdatedAt}
                  primaryColor={primaryColor}
                />
                <SubBadge status={data.parent.subscriptionStatus ?? undefined} />
              </button>
            </div>
          </CardBody>
        )}
      </Card>
    </GymDialog>
  )
}
